import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FileText, Download, Share2, Calendar, Clock, Building2, TrendingUp } from 'lucide-react'
import { Card } from '../components/common'
import { activityDb, surveyDb } from '../db/database'
import { pdf } from '@react-pdf/renderer'
import { SurveyDocument } from '../pdf/templates/SurveyDocument'
import useSurveyStore from '../stores/surveyStore'

// Format date/time for display
function formatDateTime(isoString) {
  const date = new Date(isoString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  // Relative time for recent items
  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`

  // Absolute date/time for older items
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Activity type icon and label
function getActivityInfo(type) {
  switch (type) {
    case 'pdf_download':
      return { icon: Download, label: 'PDF Downloaded', color: 'text-green-600', bgColor: 'bg-green-100' }
    case 'pdf_share':
      return { icon: Share2, label: 'PDF Shared', color: 'text-blue-600', bgColor: 'bg-blue-100' }
    case 'pdf_view':
      return { icon: FileText, label: 'PDF Viewed', color: 'text-purple-600', bgColor: 'bg-purple-100' }
    default:
      return { icon: FileText, label: 'Report Activity', color: 'text-slate-600', bgColor: 'bg-slate-100' }
  }
}

export default function ActivityPage() {
  const navigate = useNavigate()
  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({ total: 0, today: 0, thisWeek: 0 })
  const { loadSurvey } = useSurveyStore()

  // Load activity logs
  useEffect(() => {
    async function loadActivities() {
      try {
        const logs = await activityDb.getRecent(100)
        setActivities(logs)

        // Calculate stats
        const now = new Date()
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)

        const today = logs.filter(log => new Date(log.timestamp) >= todayStart).length
        const thisWeek = logs.filter(log => new Date(log.timestamp) >= weekStart).length

        setStats({ total: logs.length, today, thisWeek })
      } catch (error) {
        console.error('Failed to load activities:', error)
      } finally {
        setLoading(false)
      }
    }

    loadActivities()
  }, [])

  // Handle re-download
  const handleReDownload = async (activity) => {
    try {
      // Load survey data
      const survey = await surveyDb.getById(activity.surveyId)
      if (!survey) {
        alert('Survey not found')
        return
      }

      // Load survey with photos
      await loadSurvey(survey.remoteId || survey.id)

      // Navigate to review page (which has download functionality)
      navigate(`/survey/${survey.id}/review`)
    } catch (error) {
      console.error('Failed to re-download:', error)
      alert('Failed to load survey. Please try again.')
    }
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/3"></div>
          <div className="h-32 bg-slate-200 rounded"></div>
          <div className="h-24 bg-slate-200 rounded"></div>
          <div className="h-24 bg-slate-200 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Report Activity</h1>
        <p className="text-slate-600">Track generated and downloaded survey reports</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="flex items-center gap-4 p-6">
          <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <TrendingUp className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
            <div className="text-sm text-slate-600">Total Reports</div>
          </div>
        </Card>

        <Card className="flex items-center gap-4 p-6">
          <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Calendar className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{stats.today}</div>
            <div className="text-sm text-slate-600">Today</div>
          </div>
        </Card>

        <Card className="flex items-center gap-4 p-6">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Clock className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <div className="text-2xl font-bold text-slate-900">{stats.thisWeek}</div>
            <div className="text-sm text-slate-600">This Week</div>
          </div>
        </Card>
      </div>

      {/* Activity List */}
      <Card>
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-semibold text-slate-900">Recent Activity</h2>
        </div>

        {activities.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No activity yet</h3>
            <p className="text-slate-600">
              Report downloads and shares will appear here
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {activities.map((activity) => {
              const info = getActivityInfo(activity.type)
              const Icon = info.icon

              return (
                <div
                  key={activity.id}
                  className="p-6 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-10 h-10 ${info.bgColor} rounded-lg flex items-center justify-center flex-shrink-0 mt-1`}>
                      <Icon className={`w-5 h-5 ${info.color}`} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-slate-900 mb-1">
                            {activity.propertyName || 'Unknown Property'}
                          </h3>
                          <div className="flex items-center gap-3 text-sm text-slate-600">
                            <span className={`font-medium ${info.color}`}>{info.label}</span>
                            <span>•</span>
                            <span>Level {activity.surveyLevel || 1}</span>
                            <span>•</span>
                            <span>{formatDateTime(activity.timestamp)}</span>
                          </div>
                        </div>

                        {/* Actions */}
                        <button
                          onClick={() => handleReDownload(activity)}
                          className="px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors flex items-center gap-2"
                        >
                          <Download className="w-4 h-4" />
                          Re-download
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Card>
    </div>
  )
}
